import { logger } from '@gem/modules/logger';

import { ISpaceUserRoleResponse } from 'APP/model/space/spaceModel.types';
import { ServerErrorCode } from 'APP/restApi';
import { GroupServerErrorCode } from 'APP/restApi/constants/group.errors';
import {
  searchUsersAddToChatRequest,
  searchUsersForMentionRequest,
} from 'APP/wsApi/wsAppApi/requests/chatRequests';
import {
  globalSearchRequest,
  searchMessagesRequest,
  searchUsersInChatRequest,
} from 'APP/wsApi/wsAppApi/requests/searchRequests';

import { buildModelError } from '../error/errorBuilder';
import { WsRequestError } from '../error/errors/WsRequestError';
import { mapChatResponse, mapChatRole } from '../group/groupModel.b2b.mapper';
import { ISearchUsersInGroupResponse } from '../group/groupModel.types';
import { mapIMessage } from '../message/messageModel.b2b.mapper';
import { mapPersonalizedUserResponse } from '../user/userModel.b2b.mapper';
import {
  IGlobalSearch,
  IGlobalSearchOptions,
  ISearchMessagesRequest,
  ISearchMessagesResponse,
  ISearchUsersAddToGroupRequest,
  ISearchUsersAddToGroupResponse,
  ISearchUsersInGroupRequest,
  ISearchUsersInSpaceRequest,
  ISearchUsersForMentionRequest,
  ISearchUsersForMentionResponse,
} from './searchModel.types';

export const globalSearch = async (searchOptions: IGlobalSearchOptions): Promise<IGlobalSearch> => {
  return await bffGlobalSearch(searchOptions);
};

export const bffGlobalSearch = async (
  searchOptions: IGlobalSearchOptions
): Promise<IGlobalSearch> => {
  try {
    const searchResponse = await globalSearchRequest(searchOptions);

    if (searchResponse.type.case !== 'success') {
      return {
        groups: [],
        users: [],
        spaces: [],
        hasMoreResults: false,
      };
    }
    const search = searchResponse.type.value;

    return {
      groups: search.chats.map((chat) => mapChatResponse(chat)),
      users: search.users.map((user) =>
        mapPersonalizedUserResponse({
          $typeName: 'com.gem.bff.contract.PersonalizedUser',
          user,
          lastOnlineTs: BigInt(0),
        })
      ),
      spaces: [],
      hasMoreResults: !!search.cursor,
      cursor: search.cursor,
    };
  } catch (error) {
    logger.get('API').error('search.globalSearch', error);
    throw buildModelError(error);
  }
};

export const searchMessages = async (
  data: ISearchMessagesRequest
): Promise<ISearchMessagesResponse> => {
  try {
    const result = await searchMessagesRequest({
      ...data,
      chatId: BigInt(data.groupId),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
    }

    const value = result.type.value;

    if (!value) {
      return {
        results: [],
        count: 0,
      };
    }

    return {
      results: value.results
        .map((item) => ({
          message: mapIMessage(item.message!),
          highlights: item.highlights.map((highlight) => ({
            start: highlight.startInclusive,
            end: highlight.endExclusive,
          })),
          content: item.content,
        }))
        .filter(({ message }) => message),
      count: value.count,
      cursor: value.cursor,
    };
  } catch (error) {
    logger.get('API').error('search.searchMessages', error);
    throw buildModelError(error);
  }
};

// Needs Bff implementation
export const searchUserInSpace = async (
  data: ISearchUsersInSpaceRequest
): Promise<ISpaceUserRoleResponse> => {
  try {
    return {} as ISpaceUserRoleResponse;
  } catch (error) {
    logger.get('API').error('search.searchUserInSpace', error);
    throw buildModelError(error);
  }
};

export const searchUserInGroup = async (
  data: ISearchUsersInGroupRequest
): Promise<ISearchUsersInGroupResponse> => {
  try {
    const response = await searchUsersInChatRequest({
      chatId: BigInt(data.groupId),
      query: data.searchQuery,
      limit: data.limit,
      cursor: data.cursor || undefined,
    });

    if (response.type.case !== 'success') {
      return { participants: [], cursor: null };
    }

    return {
      participants: response.type.value.users.map((participant) => ({
        userId: String(participant.user?.user?.id),
        role: mapChatRole(participant.role),
      })),
      cursor: response.type.value.cursor || null,
    };
  } catch (error) {
    logger.get('API').error('search.searchUserInGroup', error);
    throw buildModelError(error);
  }
};

export const searchUsersAddToGroup = async (
  searchOptions: ISearchUsersAddToGroupRequest
): Promise<ISearchUsersAddToGroupResponse> => {
  try {
    const result = await searchUsersAddToChatRequest({
      chatId: searchOptions.groupId ? BigInt(searchOptions.groupId) : undefined,
      query: searchOptions.query,
      limit: searchOptions.limit,
      cursor: searchOptions.cursor,
    });

    if (result.type.case !== 'success') {
      return {
        users: [],
        hasMore: false,
      };
    }

    const searchResult = result.type.value;

    return {
      cursor: searchResult.cursor,
      users: searchResult.users.map((user) => mapPersonalizedUserResponse(user)),
      hasMore: !!searchResult.cursor,
    };
  } catch (error) {
    logger.get('API').error('search.searchUsersAddToGroup', error);
    throw buildModelError(error);
  }
};

export const searchUsersForMention = async (
  data: ISearchUsersForMentionRequest
): Promise<ISearchUsersForMentionResponse> => {
  try {
    const result = await searchUsersForMentionRequest({
      ...data,
      chatId: BigInt(data.groupId),
      query: data.prefix,
    });

    if (result.type.case !== 'success') {
      return { users: [] };
    }

    return {
      cursor: result.type.value.cursor,
      users: result.type.value.users.map((user) => mapPersonalizedUserResponse(user)),
    };
  } catch (error) {
    logger.get('API').error('search.searchUsersForMention', error);
    throw buildModelError(error);
  }
};
